import React from 'react';
import { T, NUMERIC, FONT_STACK } from './orders.theme';
import { COPY, money } from './orders.copy';
import { CrossIcon } from './OrdersIcons';

/**
 * Where did the money go?
 *
 * Settling an order used to be one tap that said "paid" and nothing else, so
 * at the end of a day the band could say $2,582 came in and nobody could say
 * how much of it was in the drawer and how much was sitting in EVC. The order
 * now carries `paymentAccount`, and this is where the cashier says which.
 *
 * The list is the business's own `paytoAccounts`, in its own order, with Cash
 * put first because it is the common answer and is not in that array — a cash
 * sale is the ABSENCE of a wallet, so it has no account number to show.
 *
 * Nothing is settled until one is chosen. The button names the account before
 * it commits, because "Pay" with no object is exactly the tap that gets made
 * without looking.
 */

/** Cash is not a `paytoAccounts` entry, so it is added rather than found. */
export const CASH_ACCOUNT = { label: COPY.cashAccount, account: '', isCash: true };

/** The tints the pills and the dialog share, keyed by position, not by name. */
const TINTS = [
  { bg: '#DCFCE7', ink: '#15803D' },
  { bg: '#DBEAFE', ink: '#1D4ED8' },
  { bg: '#FEF3C7', ink: '#B45309' },
  { bg: '#F3E8FF', ink: '#7E22CE' },
  { bg: '#FFE4E6', ink: '#BE123C' },
  { bg: '#CCFBF1', ink: '#0F766E' },
];

export const tintFor = (index) => TINTS[index % TINTS.length];

/**
 * Cash first, then the business's accounts exactly as it lists them.
 *
 * Anything without a label is dropped: a half-filled row in the business
 * record must not become a nameless button that stores an empty string.
 */
export function payAccountsOf(business) {
  const rows = Array.isArray(business?.paytoAccounts) ? business.paytoAccounts : [];

  return [CASH_ACCOUNT].concat(
    rows
      .filter((row) => row && String(row.label || '').trim())
      .map((row) => ({
        label: String(row.label).trim(),
        account: String(row.account || '').trim(),
      }))
  );
}

const initialsOf = (label) =>
  String(label || '?')
    .trim()
    .slice(0, 2)
    .toUpperCase();

export default function PayAccountDialog({ entry, accounts, currency, busy, onClose, onConfirm }) {
  const [chosen, setChosen] = React.useState('');

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, busy]);

  if (!entry) return null;

  // What the customer actually hands over, VAT included.
  const amount = entry.totalWithVat;

  return (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(21,24,29,0.44)',
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width: 430,
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: T.surface,
          borderRadius: 12,
          boxShadow: '0 12px 32px rgba(21,24,29,0.18)',
        }}
      >
        <div style={{ padding: '22px 24px 24px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              paddingBottom: 14,
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            <span style={{ fontSize: 19, fontWeight: 500, color: T.ink }}>
              {COPY.payTitle(entry.orderNumber)}
            </span>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                border: 'none',
                borderRadius: T.radiusControl,
                background: 'transparent',
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              <CrossIcon size={16} color={T.inkFaint} width={1.8} />
            </button>
          </div>

          <div
            style={{
              margin: '16px 0 4px',
              padding: 14,
              background: T.sunk,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusControl,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 12, color: T.inkSoft }}>{COPY.amount}</div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: '-0.6px',
                marginTop: 2,
                ...NUMERIC,
              }}
            >
              {money(amount, currency)}
            </div>
            {entry.vatAmount > 0 ? (
              <div style={{ fontSize: 11.5, color: T.inkFaint, marginTop: 2, ...NUMERIC }}>
                {COPY.includingVat(money(entry.vatAmount, currency))}
              </div>
            ) : null}
          </div>

          <div style={{ fontSize: 12.5, color: T.inkSoft, paddingTop: 14 }}>{COPY.whereDidItLand}</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12 }}>
            {accounts.map((account, index) => {
              const on = chosen === account.label;
              const tint = tintFor(index);

              return (
                <button
                  key={`${account.label}-${index}`}
                  type="button"
                  onClick={() => setChosen(account.label)}
                  disabled={busy}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '13px 14px',
                    textAlign: 'left',
                    background: on ? T.selectedBg : T.surface,
                    border: `1px solid ${on ? T.brand : T.border}`,
                    borderRadius: 10,
                    boxShadow: on ? `0 0 0 3px rgba(246,72,41,0.10)` : 'none',
                    font: 'inherit',
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: T.radiusControl,
                      background: tint.bg,
                      color: tint.ink,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {initialsOf(account.label)}
                  </span>

                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 600,
                        color: T.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {account.label}
                    </span>
                    {account.account ? (
                      <span style={{ fontSize: 12, color: T.inkFaint, ...NUMERIC }}>
                        {account.account}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: T.inkFaint }}>{COPY.drawer}</span>
                    )}
                  </span>

                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 15,
                      fontWeight: 800,
                      color: T.brand,
                      opacity: on ? 1 : 0,
                    }}
                  >
                    ✓
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => onConfirm(chosen)}
            disabled={busy || !chosen}
            style={{
              width: '100%',
              height: 48,
              marginTop: 18,
              border: 'none',
              borderRadius: 10,
              background: T.brand,
              color: T.surface,
              font: 'inherit',
              fontSize: 15,
              fontWeight: 600,
              opacity: busy || !chosen ? 0.45 : 1,
              cursor: busy || !chosen ? 'not-allowed' : 'pointer',
            }}
          >
            {chosen ? COPY.takeTo(money(amount, currency), chosen) : COPY.chooseAccount}
          </button>
        </div>
      </div>
    </div>
  );
}
